import { useState } from "react";
import { PlatformLayout } from "../layouts/PlatformLayout";
import {
  DEMO_CERT_STATUSES,
  DEMO_USER,
  getProgramById,
  AGENCY_ENTERPRISE_PLANS,
  levelColor,
  statusColor,
  type CertificationLevel,
  type ProgramStatus,
} from "../lib/certifications";
import { renewCertification } from "../lib/sentinel-api";
import { validateMapping, getMappingByProgramId } from "../lib/sentinel-mapping";

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

function StatusBadge({ status }: { status: ProgramStatus | "due_soon" | "overdue" | "renewed" }) {
  const labels: Record<string, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    expired: "Expired",
    due_soon: "Due Soon",
    overdue: "Overdue",
    renewed: "Renewed",
  };
  const color = status === "overdue" || status === "expired" ? "var(--error-500)" : status === "due_soon" ? "var(--warning-500)" : status === "renewed" || status === "completed" ? "var(--accent-500)" : "var(--neutral-400)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.25rem 0.625rem",
        borderRadius: "var(--radius-full)",
        fontSize: "0.6875rem",
        fontWeight: 600,
        background: `${color}15`,
        color,
      }}
    >
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: color }} />
      {labels[status] || status}
    </span>
  );
}

export default function RenewalsPage() {
  const [activeRenewalId, setActiveRenewalId] = useState<string | null>(null);
  const [renewalStep, setRenewalStep] = useState<"list" | "modules" | "exam" | "done">("list");
  const [renewing, setRenewing] = useState(false);

  async function handleRenew() {
    if (!activeRenewal) return;
    const program = getProgramById(activeRenewal.program_id);
    if (program) {
      const mappingError = validateMapping(program.code);
      if (mappingError) { alert(mappingError); return; }
    }
    const mapping = getMappingByProgramId(activeRenewal.program_id);
    setRenewing(true);
    await renewCertification(DEMO_USER.id, activeRenewal.program_id, 88, 3, mapping?.renewalProgramId);
    setRenewing(false);
    setRenewalStep("done");
  }

  const renewals = DEMO_CERT_STATUSES.filter((c) => c.renewal_due_date);
  const activeRenewal = activeRenewalId ? renewals.find((r) => r.program_id === activeRenewalId) : null;
  const activeProgram = activeRenewal ? getProgramById(activeRenewal.program_id) : null;

  const expiringLicenses = AGENCY_ENTERPRISE_PLANS.filter((p) => p.type === "agency").slice(0, 3);

  function getRenewalStatus(dueDate: string, certStatus: ProgramStatus): "due_soon" | "overdue" | "renewed" {
    if (certStatus === "expired") return "overdue";
    const due = new Date(dueDate);
    const now = new Date("2026-08-02");
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 180) return "due_soon";
    return "renewed";
  }

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Renewals & CEC</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Renew your certifications and licenses. Complete required CEC modules and pass the renewal exam to extend your certification validity.
        </p>
      </div>

      {!activeRenewal ? (
        <>
          {/* Individual renewal list */}
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Certification Renewals</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "var(--space-5)" }}>
            {renewals.map((cert) => {
              const program = getProgramById(cert.program_id);
              if (!program) return null;
              const rStatus = getRenewalStatus(cert.renewal_due_date!, cert.status);
              return (
                <div key={cert.program_id} className="card" style={{ padding: "var(--space-4)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "0.25rem" }}>{program.title}</div>
                      <div style={{ display: "flex", gap: "0.375rem", alignItems: "center", marginBottom: "0.25rem" }}>
                        <LevelPill level={program.level} />
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                        Renewal due: {cert.renewal_due_date}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
                      <StatusBadge status={rStatus} />
                      <button
                        onClick={() => {
                          setActiveRenewalId(cert.program_id);
                          setRenewalStep("modules");
                        }}
                        className="btn btn-primary"
                        style={{ fontSize: "0.8125rem" }}
                      >
                        Start Renewal
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* License renewals */}
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>License Renewals</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {expiringLicenses.map((license) => (
              <div key={license.id} className="card" style={{ padding: "var(--space-4)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "0.25rem" }}>{license.name}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      Expires: {license.end_date} | {license.currency}{license.price.toLocaleString()}/year
                    </div>
                  </div>
                  <button className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Renew License</button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Renewal flow */}
          <button
            onClick={() => { setActiveRenewalId(null); setRenewalStep("list"); }}
            style={{ background: "transparent", border: "none", color: "var(--accent-600)", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", marginBottom: "0.5rem" }}
          >
            &larr; Back to Renewals
          </button>
          <h2 style={{ fontSize: "1.25rem", marginBottom: "0.25rem" }}>Renewing: {activeProgram?.title}</h2>
          <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-4)" }}>
            Complete the CEC modules and renewal exam to extend your certification.
          </p>

          {/* Step indicator */}
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "var(--space-4)" }}>
            {["modules", "exam", "done"].map((step, i) => {
              const stepOrder = ["modules", "exam", "done"];
              const currentIdx = stepOrder.indexOf(renewalStep);
              const stepIdx = stepOrder.indexOf(step);
              const isActive = stepIdx <= currentIdx;
              return (
                <div key={step} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: isActive ? "var(--accent-500)" : "var(--neutral-200)",
                      color: isActive ? "#fff" : "var(--text-muted)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </div>
                  <span style={{ fontSize: "0.8125rem", fontWeight: isActive ? 600 : 400, color: isActive ? "var(--text)" : "var(--text-muted)", textTransform: "capitalize" }}>
                    {step === "done" ? "Complete" : step}
                  </span>
                  {i < 2 && <div style={{ width: "32px", height: "2px", background: stepIdx < currentIdx ? "var(--accent-500)" : "var(--neutral-200)" }} />}
                </div>
              );
            })}
          </div>

          {renewalStep === "modules" && (
            <div className="card" style={{ padding: "var(--space-4)" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>Required CEC Modules</h3>
              <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>
                Complete the continuing education modules from Wix Online Programs to qualify for renewal.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {["CEC Module 1: Architecture Updates", "CEC Module 2: New Intervention Templates", "CEC Module 3: Compliance Changes"].map((mod, i) => (
                  <div key={mod} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{mod}</span>
                    <span style={{ fontSize: "0.75rem", color: i === 0 ? "var(--accent-600)" : "var(--text-muted)", fontWeight: 600 }}>
                      {i === 0 ? "\u2713 Completed" : "Start"}
                    </span>
                  </div>
                ))}
              </div>
              <button onClick={() => setRenewalStep("exam")} className="btn btn-primary" style={{ marginTop: "var(--space-3)", fontSize: "0.8125rem" }}>
                Continue to Exam
              </button>
            </div>
          )}

          {renewalStep === "exam" && (
            <div className="card" style={{ padding: "var(--space-4)" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>Renewal Exam</h3>
              <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>
                Pass the renewal exam with a score of 80% or higher to extend your certification.
              </p>
              <div style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--bg-subtle)", marginBottom: "var(--space-3)" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.375rem" }}>Exam Details</div>
                <p style={{ fontSize: "0.875rem" }}>30 questions | 45 minutes | Passing score: 80%</p>
              </div>
              <button onClick={handleRenew} disabled={renewing} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>
                {renewing ? "Processing..." : "Launch Exam"}
              </button>
            </div>
          )}

          {renewalStep === "done" && (
            <div
              style={{
                padding: "var(--space-5)",
                borderRadius: "var(--radius-2xl)",
                background: "linear-gradient(135deg, var(--accent-50), var(--primary-50))",
                border: "1px solid var(--accent-300)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>&#10003;</div>
              <h2 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Renewal Complete!</h2>
              <p className="text-muted" style={{ marginBottom: "0.5rem" }}>
                Your certification has been extended. New renewal date: 2028-01-15
              </p>
              <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "var(--space-3)" }}>
                Your certificate and digital badge remain active.
              </p>
              <button onClick={() => { setActiveRenewalId(null); setRenewalStep("list"); }} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>
                Back to Renewals
              </button>
            </div>
          )}
        </>
      )}
    </PlatformLayout>
  );
}
